<template>
  <div>
    <Navbar />

    <section class="research-hero">
      <p class="hero-kicker">Whitepapers & Research</p>
      <h1>Norland Research Hub</h1>
      <p>
        Explore our latest market reports, strategy whitepapers, and data-driven insights.
        Complete the form before download, and we will send the report link to your email.
      </p>
    </section>

    <section class="research-main">
      <div class="research-shell">
        <aside class="research-left">
          <div ref="reportListTop" class="report-list-top-anchor" aria-hidden="true"></div>
          <div class="filter-bar" v-if="categoryOptions.length">
            <label>
              Category
              <select v-model="selectedCategory">
                <option value="all">All</option>
                <option v-for="option in categoryOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
            </label>
          </div>
          <div class="report-grid">
            <article v-for="report in displayedReports" :key="report.id" class="report-card">
              <div class="report-cover">
                <img :src="report.image" :alt="`${report.title} cover`" loading="lazy" />
              </div>
              <!-- <div class="report-badge">{{ report.category }}</div> -->
              <h3>{{ report.title }}</h3>
              <p class="report-summary">{{ report.summary }}</p>
              <p class="report-meta">{{ report.meta }}</p>
              <button type="button" class="apply-btn" :class="{ 'member-only-btn': isMemberOnly(report) }" :disabled="isMemberOnly(report)" @click="selectReport(report)">
                {{ isMemberOnly(report) ? 'member only' : 'Request Download' }}
              </button>
            </article>
          </div>
          <section class="pagination-wrap" v-if="totalReports > 0">
            <el-pagination background layout="prev, pager, next" :current-page="currentPage" :page-size="pageSize" :total="totalReports" :hide-on-single-page="true" @current-change="handlePageChange" />
          </section>
        </aside>

        <aside class="research-right">
          <form ref="downloadForm" class="download-form" @submit.prevent="submitRequest">
            <h2>Download Request</h2>
            <p class="form-note">Please complete the form below. We will send the download link within 1 business day.</p>

            <label>
              Full Name
              <input v-model.trim="form.name" type="text" placeholder="Enter your full name" required />
            </label>

            <label>
              Email
              <input v-model.trim="form.email" type="email" placeholder="name@email.com" required />
            </label>

            <label>
              Phone Number
              <input v-model.trim="form.phone" type="tel" placeholder="e.g. 0400 000 000" required />
            </label>

            <label>
              Select Report
              <select v-model="form.docId" required>
                <option disabled value="">Please select a report</option>
                <option v-for="report in displayedReports" :key="`option-${report.id}`" :value="report.docId">{{ report.title }}</option>
              </select>
            </label>

            <label>
              Notes
              <textarea v-model.trim="form.note" rows="3" placeholder="Optional: target areas or budget range"></textarea>
            </label>

            <button type="submit" class="submit-btn" :disabled="submitting">
              {{ submitting ? 'Submitting...' : 'Submit Request' }}
            </button>
            <p v-if="submitted" class="success-msg">Request submitted. The download link for "{{ lastRequestedReport }}" will be sent to your email.</p>
          </form>
        </aside>
      </div>
    </section>

    <SiteFooter />
  </div>
</template>

<script>
import SiteFooter from '@/components/layout/SiteFooter.vue'
import Navbar from '@/components/layout/Navbar.vue'
import { pubStoreSpecAction, pubRequstDoc } from '@/api/apiService'
import env from '@/api/env'

export default {
  components: { SiteFooter, Navbar },
  data() {
    return {
      reports: [],
      docCategories: [],
      currentPage: 1,
      pageSize: 8,
      totalReports: 0,
      form: {
        name: '',
        email: '',
        phone: '',
        docId: '',
        report: '',
        note: ''
      },
      selectedCategory: 'all',
      submitting: false,
      submitted: false,
      lastRequestedReport: ''
    }
  },
  created() {
    this.initializePage()
  },
  computed: {
    categoryOptions() {
      const fromApi = this.docCategories
        .map((item) => this.mapCategoryOption(item))
        .filter((option) => option && option.label)
      const seen = new Set()
      return fromApi.filter((option) => {
        const value = String(option.value || '').trim() || String(option.label || '').trim()
        if(!value || seen.has(value)) {
          return false
        }
        seen.add(value)
        return true
      })
    },
    displayedReports() {
      return this.reports
    }
  },
  methods: {
    async initializePage() {
      await this.fetchDocCategories()
      await this.fetchReports()
    },
    toNumber(value, fallback = 0) {
      const num = Number(value)
      return Number.isFinite(num) ? num : fallback
    },
    mapCategoryOption(item) {
      if(!item || typeof item !== 'object') {
        return null
      }

      const label = String(
        item.name || item.label || item.title || item.category || item.docType || ''
      ).trim()
      if(!label) {
        return null
      }

      const value = String(item.catId || item.categoryId || item.id || item._id || item.value || label).trim()
      return {
        value: value || label,
        label
      }
    },
    mapReport(item) {
      return {
        id: item.id || item._id,
        docId: item._id || item.id || '',
        category: (item && item.category && typeof item.category === 'object')
          ? (item.category.name || '')
          : (item.category || item.docType || ''),
        categoryId: (item && item.category && typeof item.category === 'object')
          ? (item.category.catId || '')
          : '',
        title: item.title || item.name || '',
        summary: item.desc,
        meta: (item.fileType || '') + ' | ' + (item.dateCreated ? new Date(item.dateCreated).toLocaleDateString() : ''),
        image: item.imageUrl,
        memberOnly: item.memberOnly !== undefined ? item.memberOnly : item.isMemberOnly,
        status: String(item.status || item.docStatus || item.state || '').toLowerCase(),
        stable: item.stable !== undefined ? item.stable : item.isStable
      }
    },
    async fetchDocCategories() {
      try {
        const ret = await pubStoreSpecAction(env.storeId, 'pubListDocCategories', {})
        const list = Array.isArray(ret)
          ? ret
          : (
            Array.isArray(ret && ret.categories)
              ? ret.categories
              : (Array.isArray(ret && ret.lst) ? ret.lst : (Array.isArray(ret && ret.list) ? ret.list : []))
          )
        this.docCategories = list

        if(this.selectedCategory !== 'all') {
          const exists = this.categoryOptions.some((option) => String((option && option.value) || '') === String(this.selectedCategory || ''))
          if(!exists) {
            this.selectedCategory = 'all'
          }
        }
      } catch(err) {
        this.docCategories = []
        console.error('Failed to load doc categories:', err)
      }
    },
    async fetchReports() {
      try {
        const offset = this.currentPage * this.pageSize - this.pageSize
        const params = {
          offset,
          limit: this.pageSize
        }
        const selected = String(this.selectedCategory || 'all')
        if(selected !== 'all') {
          const selectedOption = this.categoryOptions.find((option) => String((option && option.value) || '') === selected)
          const categoryLabel = String((selectedOption && selectedOption.label) || selected).trim()
          const categoryValue = String((selectedOption && selectedOption.value) || selected).trim()
          const hasDistinctCategoryId = Boolean(categoryValue && categoryLabel && categoryValue !== categoryLabel)

          if(hasDistinctCategoryId) {
            params.categoryId = categoryValue
          }
          params.category = categoryLabel
          params.docType = categoryLabel
        }

        const ret = await pubStoreSpecAction(env.storeId, 'pubListDocs', params)
        const list = Array.isArray(ret && ret.lst) ? ret.lst : []
        this.totalReports = this.toNumber(ret && ret.total, list.length)
        this.reports = list.map((item) => this.mapReport(item))

        const maxPage = this.totalReports > 0 ? Math.ceil(this.totalReports / this.pageSize) : 1
        if(this.currentPage > maxPage) {
          this.currentPage = 1
          await this.fetchReports()
        }
      } catch(err) {
        console.error('Failed to load docs:', err)
      }
    },
    async handlePageChange(page) {
      if(page === this.currentPage) {
        return
      }
      this.currentPage = page
      await this.fetchReports()
      this.$nextTick(() => {
        this.scrollToReportListTop()
      })
    },
    parseBoolean(value) {
      if(value === true || value === 1) {
        return true
      }
      if(value === false || value === 0 || value == null) {
        return false
      }
      const normalized = String(value).trim().toLowerCase()
      return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y'
    },
    isMemberOnly(report) {
      return this.parseBoolean(report && report.memberOnly)
    },
    scrollToDownloadForm() {
      const formEl = this.$refs.downloadForm
      if(formEl && typeof formEl.scrollIntoView === 'function') {
        formEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }
      window.scrollTo({ top: 460, behavior: 'smooth' })
    },
    scrollToReportListTop() {
      const listTopEl = this.$refs.reportListTop
      if(listTopEl && typeof listTopEl.scrollIntoView === 'function') {
        const headerOffset = 88
        const targetTop = window.pageYOffset + listTopEl.getBoundingClientRect().top - headerOffset
        window.scrollTo({ top: Math.max(targetTop, 0), behavior: 'smooth' })
        return
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    selectReport(report) {
      this.form.docId = report && report.docId ? String(report.docId) : ''
      this.form.report = report && report.title ? report.title : ''
      this.submitted = false
      this.$nextTick(() => {
        this.scrollToDownloadForm()
      })
    },
    async submitRequest() {
      if(this.submitting) {
        return
      }
      if(!this.form.name || !this.form.email || !this.form.phone || !this.form.docId) {
        return
      }
      const selectedReport = this.reports.find((item) => String(item.docId || '') === String(this.form.docId || ''))
      const payload = {
        name: this.form.name,
        email: this.form.email,
        phone: this.form.phone,
        docId: this.form.docId,
        report: selectedReport ? selectedReport.title : this.form.report,
        note: this.form.note
      }
      this.submitting = true
      try {
        await pubRequstDoc(env.storeId, payload)
        this.lastRequestedReport = selectedReport ? selectedReport.title : this.form.report
        this.submitted = true
        this.$alert('Request submitted successfully. We will send the report link to your email soon.', 'Success', {
          confirmButtonText: 'OK',
          type: 'success'
        })
        this.form = {
          name: '',
          email: '',
          phone: '',
          docId: '',
          report: '',
          note: ''
        }
      } catch(err) {
        this.submitted = false
        console.error('Failed to submit document request:', err)
      } finally {
        this.submitting = false
      }
    }
  },
  watch: {
    'form.docId'(nextDocId) {
      const selectedReport = this.displayedReports.find((item) => String(item.docId || '') === String(nextDocId || ''))
      this.form.report = selectedReport ? selectedReport.title : ''
    },
    async selectedCategory() {
      this.currentPage = 1
      await this.fetchReports()
      const exists = this.displayedReports.some((report) => String(report.docId || '') === String(this.form.docId || ''))
      if(!exists) {
        this.form.docId = ''
        this.form.report = ''
      }
    }
  }
}
</script>

<style scoped>
.research-hero {
  text-align: center;
  padding: 108px 4% 88px;
  background:
    radial-gradient(circle at 12% 12%, rgba(138, 190, 176, 0.2), transparent 34%),
    radial-gradient(circle at 86% 84%, rgba(96, 143, 132, 0.14), transparent 28%),
    #edf7f3;
}

.hero-kicker {
  margin: 0;
  color: #3f8a79;
  font-size: 0.82rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: 700;
}

.research-hero h1 {
  margin: 14px 0 10px;
  color: #2c5f54;
  font-size: clamp(2.2rem, 5vw, 4.4rem);
  line-height: 1.04;
}

.research-hero p {
  margin: 0 auto;
  max-width: 840px;
  color: #5c7971;
  line-height: 1.7;
}

.research-main {
  padding: 34px 5% 80px;
  background: linear-gradient(180deg, #edf7f3 0%, #f3faf7 100%);
}

.research-shell {
  max-width: 1260px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(320px, 0.8fr);
  gap: 22px;
  align-items: start;
}

.research-left,
.research-right {
  border-radius: 16px;
}

.filter-bar {
  margin-bottom: 14px;
  display: flex;
  justify-content: flex-start;
}

.filter-bar label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #446a61;
  font-weight: 600;
}

.filter-bar select {
  border: 1px solid #c5ddd6;
  border-radius: 10px;
  padding: 8px 10px;
  background: #f9fdfb;
  color: #335f55;
}

.report-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.pagination-wrap {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}

.report-list-top-anchor {
  height: 0;
}

.report-card {
  border: 1px solid #d2e6e0;
  border-radius: 14px;
  padding: 12px;
  background: #fffdf9;
  display: grid;
  gap: 8px;
}

.report-cover {
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 4px;
  background: #edf4f1;
}

.report-cover img {
  width: 100%;
  height: 138px;
  object-fit: contain;
  object-position: center;
  display: block;
}

.report-badge {
  display: inline-flex;
  width: fit-content;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  color: #3f8a79;
  background: #e7f4f0;
  border: 1px solid #c5e1d9;
}

.report-card h3 {
  margin: 0;
  color: #2f5f55;
  font-size: 1.14rem;
}

.report-summary {
  margin: 0;
  color: #5c7971;
  line-height: 1.6;
}

.report-meta {
  margin: 0;
  color: #5e9b8d;
  font-size: 0.82rem;
  letter-spacing: 0.03em;
}

.apply-btn {
  margin-top: 6px;
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  background: #4e907f;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}

.member-only-btn {
  background: #b98524;
  color: #fff6e7;
  cursor: not-allowed;
}

.member-only-btn:disabled {
  opacity: 0.95;
}

.download-form {
  position: sticky;
  top: 96px;
  margin-top: 50px;
  border: 1px solid #b9d7cf;
  border-radius: 16px;
  padding: 18px;
  background: linear-gradient(160deg, #2c7a6a 0%, #205f53 100%);
  color: #edf8f5;
  display: grid;
  gap: 10px;
  min-width: 0;
}

.download-form h2 {
  margin: 0;
  font-size: 1.4rem;
}

.form-note {
  margin: 0 0 4px;
  color: rgba(237, 248, 245, 0.84);
  font-size: 0.9rem;
}

.download-form label {
  display: grid;
  gap: 6px;
  font-size: 0.88rem;
  min-width: 0;
}

.download-form input,
.download-form select,
.download-form textarea {
  border: 1px solid #8dc1b4;
  border-radius: 10px;
  padding: 9px 10px;
  background: #f8fdfb;
  color: #2f5f55;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  display: block;
}

.checkbox-label {
  display: flex !important;
  align-items: flex-start;
  gap: 8px;
  color: #d9efe9;
}

.checkbox-label input {
  margin-top: 2px;
}

.submit-btn {
  border: none;
  border-radius: 10px;
  padding: 10px 12px;
  background: #7fb4a7;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}

.submit-btn:disabled {
  opacity: 0.8;
  cursor: not-allowed;
}

.success-msg {
  margin: 2px 0 0;
  color: #d4eee7;
  font-size: 0.88rem;
  line-height: 1.5;
}

@media (max-width: 1024px) {
  .research-shell {
    grid-template-columns: 1fr;
  }

  .download-form {
    position: static;
    margin-top: 0;
  }
}

@media (max-width: 768px) {
  .research-hero {
    padding: 58px 4% 34px;
  }

  .research-hero h1 {
    color: #28564c;
  }

  .research-hero p {
    color: #4f7168;
  }

  .research-main {
    padding: 24px 4% 56px;
  }

  .filter-bar {
    justify-content: flex-start;
  }

  .report-grid {
    grid-template-columns: 1fr;
  }

  .report-card h3 {
    color: #2a584f;
  }

  .report-summary {
    color: #4f746a;
  }

  .apply-btn {
    background: #3f816f;
  }

  .member-only-btn,
  .member-only-btn:disabled {
    background: #8a4f06;
    color: #fff1dc;
  }

  .download-form label {
    color: #e7f4f0;
  }

  .submit-btn {
    background: #5d9f8f;
  }
}
</style>
